<script>
export default {
  props: {
    customAttributes: { type: Array, required: true },
    modelValue: { type: Object, required: true },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      selectedAttribute: null,
      attributeValue: '',
    };
  },
  computed: {
    selectedAttributeType() {
      return this.selectedAttribute?.attribute_display_type || 'text';
    },
    isListType() {
      return this.selectedAttributeType === 'list';
    },
    isCheckboxType() {
      return this.selectedAttributeType === 'checkbox';
    },
    listOptions() {
      if (!this.isListType || !this.selectedAttribute?.attribute_values) {
        return [];
      }
      return this.selectedAttribute.attribute_values.map(value => ({
        id: value,
        name: value,
      }));
    },
    checkboxOptions() {
      return [
        { id: 'true', name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE') },
        { id: 'false', name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE') },
      ];
    },
    selectedListValue: {
      get() {
        if (!this.isListType) return null;
        return this.listOptions.find(opt => opt.id === this.attributeValue);
      },
      set(value) {
        this.attributeValue = value?.id || '';
        this.updateValue();
      },
    },
    selectedCheckboxValue: {
      get() {
        if (!this.isCheckboxType) return null;
        return this.checkboxOptions.find(
          opt => opt.id === String(this.attributeValue)
        );
      },
      set(value) {
        this.attributeValue = value?.id === 'true';
        this.updateValue();
      },
    },
  },
  watch: {
    modelValue: {
      handler(newValue) {
        if (newValue && typeof newValue === 'object') {
          const { key, value } = newValue;
          if (key && this.selectedAttribute?.id !== key) {
            this.selectedAttribute = this.customAttributes.find(
              attr => attr.id === key
            );
          }
          if (value !== this.attributeValue) {
            this.attributeValue = value || '';
          }
        }
      },
      deep: true,
    },
    customAttributes: {
      handler() {
        if (this.customAttributes.length > 0 && !this.selectedAttribute) {
          this.loadInitialValues();
        }
      },
      immediate: true,
    },
    selectedAttribute(newAttr, oldAttr) {
      if (newAttr?.id !== oldAttr?.id) {
        this.attributeValue = '';
        this.updateValue();
      }
    },
  },
  mounted() {
    this.loadInitialValues();
  },
  methods: {
    loadInitialValues() {
      if (this.modelValue && typeof this.modelValue === 'object') {
        const { key, value } = this.modelValue;
        if (key) {
          this.selectedAttribute = this.customAttributes.find(
            attr => attr.id === key
          );
          this.attributeValue = value !== undefined ? value : '';
        }
      }
    },
    updateValue() {
      this.$emit('update:modelValue', {
        key: this.selectedAttribute?.id,
        value: this.attributeValue,
      });
    },
  },
};
</script>

<template>
  <div>
    <div class="multiselect-wrap--small flex flex-col gap-1 mt-1">
      <multiselect
        v-model="selectedAttribute"
        track-by="id"
        label="name"
        :placeholder="
          $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_DROPDOWN_PLACEHOLDER')
        "
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :max-height="160"
        :options="customAttributes"
        :allow-empty="false"
        @update:model-value="updateValue"
      >
        <template #noOptions>
          {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
        </template>
      </multiselect>

      <multiselect
        v-if="isListType && selectedAttribute"
        v-model="selectedListValue"
        track-by="id"
        label="name"
        :placeholder="
          $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
        "
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :max-height="160"
        :options="listOptions"
        :allow-empty="false"
      >
        <template #noOptions>
          {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
        </template>
      </multiselect>

      <multiselect
        v-else-if="isCheckboxType && selectedAttribute"
        v-model="selectedCheckboxValue"
        track-by="id"
        label="name"
        :placeholder="
          $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
        "
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :max-height="160"
        :options="checkboxOptions"
        :allow-empty="false"
      >
        <template #noOptions>
          {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
        </template>
      </multiselect>

      <input
        v-else-if="selectedAttribute"
        v-model="attributeValue"
        type="text"
        class="answer--text-input"
        :placeholder="
          $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
        "
        @input="updateValue"
      />
    </div>
  </div>
</template>
